import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../contexts/auth-context'
import { queryKeys } from '../../../lib/query-keys'

interface ClientDashboardData {
  clientName: string
  activeEngagements: number
  pendingDocuments: number
  upcomingDeadlines: number
  unreadMessages: number
}

export function useClientDashboard() {
  const { clientId } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.portal.dashboard(clientId!),
    queryFn: async () => {
      // First get client name and engagement IDs (needed for subsequent queries)
      const [clientResult, engagementsResult] = await Promise.all([
        (supabase as any).from('clients').select('name').eq('id', clientId).single(),
        (supabase as any).from('engagements').select('id, taxpayer:taxpayers!inner(client_id)').eq('taxpayer.client_id', clientId),
      ])

      const engagementIds = engagementsResult.data?.map((e: any) => e.id) || []

      // Now run all count queries in parallel
      const thirtyDaysOut = new Date()
      thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30)

      const [engCount, docCount, filingCount, msgCount] = await Promise.all([
        (supabase as any).from('engagements').select('id, taxpayer:taxpayers!inner(client_id)', { count: 'exact', head: true })
          .eq('taxpayer.client_id', clientId).not('status', 'in', '("draft","cancelled","completed")'),
        engagementIds.length > 0
          ? (supabase as any).from('doc_requests').select('id', { count: 'exact', head: true })
              .in('engagement_id', engagementIds).eq('status', 'sent')
          : Promise.resolve({ count: 0 }),
        engagementIds.length > 0
          ? (supabase as any).from('filing_periods').select('id', { count: 'exact', head: true })
              .in('engagement_id', engagementIds).neq('status', 'filed')
              .lte('due_date', thirtyDaysOut.toISOString().split('T')[0])
          : Promise.resolve({ count: 0 }),
        (supabase as any).from('client_messages').select('id', { count: 'exact', head: true })
          .eq('client_id', clientId).eq('sender_type', 'staff').is('read_at', null),
      ])

      return {
        clientName: clientResult.data?.name || 'Client',
        activeEngagements: engCount.count || 0,
        pendingDocuments: docCount.count || 0,
        upcomingDeadlines: filingCount.count || 0,
        unreadMessages: msgCount.count || 0,
      } as ClientDashboardData
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,  // 5 min for dashboard
  })

  return { data: data ?? null, loading: isLoading }
}
